// quantity-bundle.js — custom element.
//
// Era uma classe solta + auto-init no DOMContentLoaded. Isso quebrava na troca de
// sabor: o tp-sabores substitui o innerHTML do <product-info> e re-executa os
// scripts, e `class QuantityBundle` no topo do escopo global estourava
// "Identifier 'QuantityBundle' has already been declared", matando o arquivo
// inteiro — o bundle novo nunca inicializava.
//
// Como custom element, o connectedCallback roda sozinho quando o elemento entra
// no DOM (innerHTML incluso) e o guard abaixo evita a redeclaração.

if (!customElements.get("quantity-bundle")) {
  class QuantityBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      // Dados essenciais vindos do Liquid
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.variantAvailable = this.dataset.variantAvailable === "true";
      this.productHandle = this.dataset.productHandle;

      // Elementos importantes
      this.radioButtons = this.querySelectorAll(
        ".quantity-bundle__radio-input",
      );
      this.buyButton = this.querySelector(".quantity-bundle__buy-button");
      this.unitPriceEl = this.querySelector(".quantity-bundle__price-unit");
      this.comparePriceEl = this.querySelector(
        ".quantity-bundle__compare-price",
      );
      this.currentPriceEl = this.querySelector(
        ".quantity-bundle__current-price",
      );
      this.pixPriceEl = this.querySelector(".quantity-bundle__pix-price");
      this.parcelsPriceEl = this.querySelector(
        ".quantity-bundle__price-parcels",
      );
      this.tagDiscountEl = this.querySelector(".quantity-bundle__discount-tag");

      // Sem os elementos de preço não há o que inicializar. Sai sem estourar,
      // senão um render parcial derruba o script todo de novo.
      if (!this.buyButton || !this.currentPriceEl) {
        console.warn("[quantity-bundle] markup incompleto, init abortado");
        return;
      }

      this.initialized = true;

      // O bundle não é re-renderizado pelo product-info.js quando a variante muda,
      // então escutamos o evento para não mandar a variante errada ao carrinho.
      this.subscribeToVariantChange();

      // === valores vindos prontos do Liquid ===
      this.parcelas = parseFloat(this.parcelsPriceEl?.dataset.parcelas || 1);

      // === processar descontos enviados via data-discounts ===
      const discountStr = this.dataset.discounts || "0";
      this.discounts = discountStr.split("/").map((v) => parseFloat(v));

      // === capturar quantidades diretamente do HTML ===
      this.quantities = [
        ...this.querySelectorAll(".quantity-bundle__option"),
      ].map((opt) => parseInt(opt.dataset.quantity));

      // Preço base do produto (centavos → reais)
      this.basePrice =
        parseFloat(this.currentPriceEl.dataset.currentPrice) / 100;

      // Desconto PIX (%)
      this.pixDiscount = parseFloat(this.pixPriceEl?.dataset.pixDiscount || 0);

      // Estado inicial
      const { value, position } = this.getSelectedQuantityAndPosition();

      this.selectedQuantity = value;
      this.position = position;

      this.init();
    }

    disconnectedCallback() {
      // Sem isso cada troca de sabor deixaria um subscriber morto acumulado
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
        this.variantChangeUnsubscriber = null;
      }
    }

    init() {
      this.bindEvents();
      this.updatePrices();
      this.setupButton();
    }

    setupButton() {
      this.originalButtonText = this.buyButton.textContent;
    }

    bindEvents() {
      this.radioButtons.forEach((radio) => {
        radio.addEventListener("change", () => {
          this.selectedQuantity = parseInt(radio.value);
          this.position = parseInt(radio.dataset.position);
          this.updatePrices();
          this.updateVisualSelection();
        });
      });

      this.querySelectorAll(".quantity-bundle__option").forEach((option) => {
        option.addEventListener("click", (e) => {
          if (e.target.type !== "radio") {
            const radio = option.querySelector(".quantity-bundle__radio-input");
            if (radio) {
              radio.checked = true;
              radio.dispatchEvent(new Event("change"));
            }
          }
        });
      });

      this.buyButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    getSelectedQuantityAndPosition() {
      const checked = this.querySelector(
        ".quantity-bundle__radio-input:checked",
      );

      // Sem radio marcado o acesso a .dataset estourava e derrubava o init
      if (!checked) {
        return { value: this.quantities[0] || 1, position: 1 };
      }

      return {
        value: parseInt(checked.value),
        position: checked.dataset.position,
      };
    }

    updatePrices() {
      const qtySelected = this.selectedQuantity;
      const positionSelected = this.position;

      // O index e a posicao em que aparecem as opcoes, nao o index da quantidade
      const index = positionSelected - 1;
      const discountPercentRaw = this.discounts[index] || 0;

      // Arredondar o desconto para inteiro
      const discountPercent = Math.round(discountPercentRaw);

      const discountDecimal = (100 - discountPercentRaw) / 100;

      // Preco total com desconto
      const totalCurrent = this.basePrice * qtySelected * discountDecimal;

      // Comparacao (sem desconto)
      const totalCompare = this.basePrice * qtySelected;

      // Preco unidade
      const priceUnit = totalCurrent / qtySelected;

      // Pix
      const pixDecimal = (100 - this.pixDiscount) / 100;
      const pixPrice = totalCurrent * pixDecimal;

      // Parcelado
      const parcelasValue = totalCurrent / this.parcelas;

      // Atualizar precos no DOM
      if (this.unitPriceEl) {
        this.unitPriceEl.textContent = `${this.formatPrice(priceUnit)} cada`;
      }
      this.currentPriceEl.textContent = this.formatPrice(totalCurrent);
      if (this.parcelsPriceEl) {
        this.parcelsPriceEl.textContent = `ou ${
          this.parcelas
        }x de ${this.formatPrice(parcelasValue)}`;
      }
      if (this.pixPriceEl) {
        this.pixPriceEl.innerHTML = `${this.formatPrice(
          pixPrice,
        )} ${this.pixPriceEl.textContent.split(" ").slice(-2).join(" ")}`;
      }

      // Aplicar valor arredondado na tag
      if (this.tagDiscountEl) {
        this.tagDiscountEl.textContent = `${discountPercent}% OFF`;
      }

      // Mostrar ou esconder elementos
      if (discountPercent <= 0) {
        if (this.comparePriceEl) this.comparePriceEl.style.display = "none";
        if (this.tagDiscountEl) this.tagDiscountEl.style.display = "none";
      } else {
        if (this.tagDiscountEl) this.tagDiscountEl.style.display = "";
        if (this.comparePriceEl) {
          this.comparePriceEl.style.display = "";
          this.comparePriceEl.textContent = this.formatPrice(totalCompare);
        }
      }
    }

    updateComparePriceVisibility(index) {
      if (!this.comparePriceEl) return;
      this.comparePriceEl.style.display = index === 0 ? "none" : "";
    }

    updateVisualSelection() {
      this.querySelectorAll(".quantity-bundle__option").forEach((opt) =>
        opt.classList.remove("quantity-bundle__option--selected"),
      );

      const selected = this.querySelector(
        ".quantity-bundle__radio-input:checked",
      );
      if (selected) {
        selected
          .closest(".quantity-bundle__option")
          .classList.add("quantity-bundle__option--selected");
      }
    }

    formatPrice(price) {
      return "R$ " + price.toFixed(2).replace(".", ",");
    }

    subscribeToVariantChange() {
      if (
        typeof subscribe !== "function" ||
        typeof PUB_SUB_EVENTS === "undefined"
      ) {
        return;
      }

      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        (event) => {
          const variant = event?.data?.variant;
          if (!variant) return;

          this.variantId = variant.id;
          this.variantAvailable = variant.available === true;
          this.dataset.variantId = variant.id;
          this.dataset.variantAvailable = String(this.variantAvailable);

          if (this.buyButton) {
            this.buyButton.disabled = !this.variantAvailable;
          }
        },
      );
    }

    async addToCart() {
      if (this.buyButton.disabled) return;

      if (!this.variantId) {
        console.error("[quantity-bundle] sem variante selecionada");
        return;
      }

      try {
        this.buyButton.disabled = true;
        this.buyButton.textContent = "Adicionando...";

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: this.variantId,
            quantity: this.selectedQuantity,
          }),
        });

        if (!response.ok) throw new Error("Erro ao adicionar ao carrinho");

        this.showSuccessFeedback();
        setTimeout(() => this.openCartDrawer(), 300);
      } catch (err) {
        console.error(err);
        this.showErrorFeedback();
      }
    }

    showSuccessFeedback() {
      this.buyButton.textContent = "\u2713 Adicionado!";
      setTimeout(() => this.restoreButton(), 2000);
    }

    showErrorFeedback() {
      this.buyButton.textContent = "Erro - tente novamente";
      setTimeout(() => this.restoreButton(), 2500);
    }

    restoreButton() {
      this.buyButton.disabled = false;
      this.buyButton.textContent = this.originalButtonText;
    }

    async openCartDrawer() {
      const response = await fetch("/?sections=cart-drawer,cart-icon-bubble");
      const sections = await response.json();
      const parsedState = { sections };

      const drawer = document.querySelector("cart-drawer");
      if (!drawer) return;

      drawer.classList.remove("is-empty");
      drawer.renderContents(parsedState);

      requestAnimationFrame(() => {
        drawer.classList.add("animate", "active");
        document.body.classList.add("overflow-hidden");
      });
    }
  }

  customElements.define("quantity-bundle", QuantityBundle);
}